import { useState } from 'react';
import { useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { Empty, ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { MerchantPicker } from './pickers.tsx';
import { ORIGIN_LABELS, type AdSpaceList, type Named, type ValueMatrix } from './types.ts';

/** Network × country × site: what every parameter resolves to and from which level. */
export function ValueMatrixView({ networkRef, countries, defaultCountry }: { networkRef: string; countries: { code: string; name: string }[]; defaultCountry?: string }) {
  const changes = useChanges();
  const [country, setCountry] = useState(defaultCountry ?? countries[0]?.code ?? 'GB');
  const [merchant, setMerchant] = useState<Named | null>(null);
  const matrix = useOp<ValueMatrix>('admin.deeplinks.matrix', { networkRef, country, ...(merchant ? { merchantRef: merchant.ref } : {}) }, changes);
  const data = matrix.data;

  return (
    <div>
      <div className="aff-picker">
        <label>
          Country
          <select
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setMerchant(null);
            }}
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <MerchantPicker value={merchant} onChange={setMerchant} market={country} label="Merchant (optional)" />
      </div>
      {matrix.error ? <ErrorBox error={matrix.error} onRetry={matrix.reload} /> : null}
      {data?.merchant ? (
        <p className="muted small">
          {data.program ? (
            <>
              Program: <strong>{data.program.name}</strong> · id <span className="mono">{data.program.networkProgramId}</span>
            </>
          ) : (
            <>No program of {data.merchant.name} on this network: parameters filled from the program stay empty.</>
          )}
        </p>
      ) : null}
      {!data && matrix.loading ? <Loading /> : null}
      {data && data.rows.length === 0 ? <Empty>No sites of yours in this country.</Empty> : null}
      {data && data.rows.length > 0 ? (
        <div className="aff-matrix-wrap">
          <table className="aff-matrix">
            <thead>
              <tr>
                <th scope="col">Site</th>
                <th scope="col">Ready</th>
                {data.parameters.map((p) => (
                  <th key={p.id} scope="col">
                    <span className="mono">{p.parameter}</span>
                    <small>{p.fromProgram ? 'from program' : p.fillFrom === 'ad_space' ? 'from ad space' : p.fillFrom === 'account' ? 'from account' : p.levelLabel}</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.site.ref}>
                  <th scope="row">
                    {row.site.name}
                    {data.merchant && !row.merchantOnSite ? <small className="muted"> · merchant not on site</small> : null}
                  </th>
                  <td>{row.ready ? <span className="badge badge-ok">ready</span> : <span className="badge badge-warn">missing values</span>}</td>
                  {row.cells.map((cell) => {
                    const soft = ['deep_url', 'fallback_url', 'click_ref', 'per_merchant'].includes(cell.origin) || (cell.origin === 'program' && cell.value === null);
                    return (
                      <td key={cell.parameterId} className={cell.blocking ? 'aff-missing' : undefined}>
                        <span className={`aff-cell ${cell.blocking ? 'blocking' : ''} ${soft ? 'soft' : ''}`} title={cell.value ?? ''}>
                          <span className="v">{cell.blocking ? 'missing' : soft ? ORIGIN_LABELS[cell.origin] : (cell.value ?? '—')}</span>
                          {!soft && !cell.blocking ? <small>{ORIGIN_LABELS[cell.origin]}</small> : null}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {data && !data.merchant ? <p className="muted small">Pick a merchant to resolve merchant-level values (program ids and the like).</p> : null}
    </div>
  );
}

/** Ad space ids per site × network; empty cells mean that site cannot get links on that network. Edits save on Enter or when leaving the field. */
export function AdSpaces() {
  const changes = useChanges();
  const list = useOp<AdSpaceList>('admin.adSpaces.list', {}, changes);
  const [useAsValue, setUseAsValue] = useState(true);
  if (list.error) return <ErrorBox error={list.error} onRetry={list.reload} />;
  if (!list.data) return <Loading />;
  const { networks, sites, cells } = list.data;
  return (
    <div>
      <label className="inline-check" style={{ marginBottom: 8 }}>
        <input type="checkbox" checked={useAsValue} onChange={(e) => setUseAsValue(e.target.checked)} /> Also use the first id as the site’s value of the network’s site parameter
      </label>
      <div className="aff-matrix-wrap">
        <table className="aff-matrix">
          <thead>
            <tr>
              <th scope="col">Network</th>
              {sites.map((s) => (
                <th key={s.ref} scope="col">
                  {s.name}
                  <small>{s.country}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {networks.map((n) => (
              <tr key={n.ref}>
                <th scope="row">
                  {n.name}
                  <small className="muted" style={{ display: 'block', fontWeight: 400 }}>
                    {n.siteParameter ? `site parameter: ${n.siteParameter}` : 'no single site parameter'}
                  </small>
                </th>
                {sites.map((s) => {
                  const cell = cells.find((c) => c.networkRef === n.ref && c.siteRef === s.ref)!;
                  return (
                    <td key={s.ref} className={cell.adSpaces ? undefined : 'aff-missing'}>
                      <AdSpaceCell network={n} site={s} adSpaces={cell.adSpaces} siteValue={cell.siteValue} useAsValue={useAsValue} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdSpaceCell({ network, site, adSpaces, siteValue, useAsValue }: { network: Named; site: Named & { canManage: boolean }; adSpaces: string | null; siteValue: string | null; useAsValue: boolean }) {
  const toast = useToast();
  const [draft, setDraft] = useState(adSpaces ?? '');
  const save = () => {
    if (draft.trim() === (adSpaces ?? '')) return;
    void runWrite<{ siteParameter: string | null; cleared: boolean }>(
      toast,
      'admin.adSpaces.save',
      { networkRef: network.ref, siteRef: site.ref, adSpaces: draft.trim(), useAsSiteValue: useAsValue },
      (r) => (r.cleared ? `${site.name} on ${network.name}: ad space removed` : `${site.name} on ${network.name}: saved${r.siteParameter ? ` (also ${r.siteParameter})` : ''}`),
    );
  };
  return (
    <span className="aff-cell">
      <input
        className="mono"
        value={draft}
        readOnly={!site.canManage}
        placeholder={site.canManage ? 'missing' : '—'}
        aria-label={`Ad space of ${site.name} on ${network.name}`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setDraft(adSpaces ?? '');
        }}
      />
      {siteValue && siteValue !== (adSpaces ?? '').split(/[\s,;]+/)[0] ? <small>site value: {siteValue}</small> : null}
    </span>
  );
}
